/**
 * SecureMessageView — contact's view of a secure message thread.
 *
 * Shows the full thread, reply box, and attachment downloads.
 * Auto-logout after 30 minutes of inactivity.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  messages as messagesApi,
  MessageDetail,
  AttachmentMeta,
  validateAttachment,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_COUNT,
} from '../../lib/api.js';
import { useAuth } from '../../lib/auth-context.js';
import MessageBody from '../../components/MessageBody.js';

interface Props {
  messageId: string;
}

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function SecureMessageView({ messageId }: Props): React.ReactElement {
  const { logout } = useAuth();
  const [detail,     setDetail]    = useState<MessageDetail | null>(null);
  const [loading,    setLoading]   = useState(true);
  const [error,      setError]     = useState<string | null>(null);
  const [replyBody,  setReplyBody] = useState('');
  const [replying,   setReplying]  = useState(false);
  const [replyError, setReplyErr]  = useState<string | null>(null);
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Inactivity timer — auto-logout after 30 minutes of no interaction
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetInactivity = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      logout();
    }, INACTIVITY_MS);
  }, [logout]);

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, resetInactivity));
    resetInactivity(); // Start timer immediately
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetInactivity));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [resetInactivity]);

  // silent=true refreshes in the background (live-update poll) without the
  // loading flash replacing the visible thread.
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await messagesApi.read(messageId);
      if (res.success && res.data) {
        setDetail(res.data.message);
      } else if (!silent) {
        setError(res.error ?? 'Failed to load message');
      }
    } catch {
      if (!silent) setError('Failed to load message');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [messageId]);

  useEffect(() => { void load(); }, [load]);

  // Live updates: poll the cheap meta probe (no PHI, no audit) and re-fetch the
  // full thread only when the reply count changed. Paused while the tab is hidden.
  const replyCountRef = useRef<number | null>(null);
  useEffect(() => {
    replyCountRef.current = detail ? detail.replies.length : null;
  }, [detail]);

  useEffect(() => {
    const timer = setInterval(async () => {
      if (document.hidden || replyCountRef.current === null) return;
      try {
        const res = await messagesApi.meta(messageId);
        if (res.success && res.data && res.data.replyCount !== replyCountRef.current) {
          await load({ silent: true });
        }
      } catch { /* transient poll failure — next tick retries */ }
    }, 15_000);
    return () => clearInterval(timer);
  }, [messageId, load]);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = ''; // allow re-picking same file
    if (!picked.length) return;
    const errors: string[] = [];
    const valid: File[] = [];
    for (const f of picked) {
      const err = validateAttachment(f);
      if (err) errors.push(err); else valid.push(f);
    }
    let combined = [...replyFiles, ...valid];
    if (combined.length > ATTACHMENT_MAX_COUNT) {
      errors.push(`You can attach at most ${ATTACHMENT_MAX_COUNT} files.`);
      combined = combined.slice(0, ATTACHMENT_MAX_COUNT);
    }
    setReplyFiles(combined);
    setReplyErr(errors.length ? errors.join(' ') : null);
  };

  const removeFile = (idx: number) => {
    setReplyFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleReply = async () => {
    if (!replyBody.trim()) { setReplyErr('Reply cannot be empty'); return; }
    setReplying(true);
    setReplyErr(null);
    try {
      const res = await messagesApi.reply(messageId, replyBody.trim());
      if (!res.success || !res.data) {
        setReplyErr(res.error ?? 'Failed to send reply');
        return;
      }
      // Two-step (non-atomic): upload each file against the new reply.
      const replyId = res.data.replyId;
      const failed: string[] = [];
      for (const f of replyFiles) {
        const up = await messagesApi.attach(messageId, f, replyId);
        if (!up.success) failed.push(f.name);
      }
      setReplyBody('');
      setReplyFiles([]);
      await load();
      if (failed.length) {
        setReplyErr(`Your reply was sent, but ${failed.length} file(s) didn't upload: ${failed.join(', ')}. Please try attaching them again.`);
      }
    } catch {
      setReplyErr('Unexpected error. Please try again.');
    } finally {
      setReplying(false);
    }
  };

  const handleDownload = async (attachmentId: string) => {
    const res = await messagesApi.downloadUrl(messageId, attachmentId);
    if (res.success && res.data) window.open(res.data.url, '_blank');
  };

  if (loading) {
    return (
      <div style={styles.fullCenter}>
        <p style={styles.muted}>Loading your message…</p>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div style={styles.fullCenter}>
        <div style={styles.card}>
          <p style={{ color: '#c0392b', margin: 0 }}>{error ?? 'Message not found'}</p>
        </div>
      </div>
    );
  }

  const allEntries = [
    { id: detail.id, fromType: detail.fromType, body: detail.body, createdAt: detail.createdAt, attachments: detail.attachments },
    ...detail.replies.map((r) => ({ id: r.id, fromType: r.fromType, body: r.body, createdAt: r.createdAt, attachments: r.attachments })),
  ];

  const renderAttachments = (list: AttachmentMeta[]) =>
    list.length > 0 ? (
      <div style={styles.bubbleAttachments}>
        {list.map((a) => (
          <button
            key={a.id}
            style={styles.attachmentChip}
            onClick={() => handleDownload(a.id)}
            title={`Download ${a.filename}`}
          >
            📎 {a.filename}{' '}
            <span style={styles.attachSize}>({(a.sizeBytes / 1024).toFixed(0)} KB)</span>
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div style={styles.page}>
      {/* Secure banner */}
      <div style={styles.secureBanner}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <rect x="3" y="11" width="18" height="11" rx="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          This conversation is encrypted and secure
        </span>
        <button style={styles.logoutBtn} onClick={logout} title="End session">
          Log out
        </button>
      </div>

      <div style={styles.container}>
        {/* Subject header */}
        <div style={styles.header}>
          <h1 style={styles.subject}>{detail.subject}</h1>
          <p style={styles.headerMeta}>Received {formatDate(detail.createdAt)}</p>
        </div>

        {/* Message thread */}
        <div style={styles.thread}>
          {allEntries.map((entry) => {
            const isStaff = entry.fromType === 'staff';
            return (
              <div
                key={entry.id}
                style={{ ...styles.bubble, ...(isStaff ? styles.bubbleStaff : styles.bubblePatient) }}
              >
                <div style={styles.bubbleHeader}>
                  <span style={styles.sender}>{isStaff ? `From ${detail.fromName || 'your provider'}` : 'You'}</span>
                  <span style={styles.bubbleTime}>{formatDate(entry.createdAt)}</span>
                </div>
                <MessageBody body={entry.body} style={styles.bubbleBody} />
                {renderAttachments(entry.attachments)}
              </div>
            );
          })}
        </div>

        {/* Reply box */}
        <div style={styles.replySection}>
          <h3 style={styles.replyTitle}>Send a Reply</h3>
          {replyError && <div style={styles.errorBox}>{replyError}</div>}
          <textarea
            style={styles.textarea}
            placeholder="Type your reply here…"
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            disabled={replying}
            rows={5}
          />
          {replyFiles.length > 0 && (
            <div style={styles.fileChips}>
              {replyFiles.map((f, i) => (
                <span key={`${f.name}-${i}`} style={styles.fileChip}>
                  📎 {f.name}
                  <button
                    style={styles.fileChipRemove}
                    onClick={() => removeFile(i)}
                    disabled={replying}
                    aria-label={`Remove ${f.name}`}
                    type="button"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT}
            style={{ display: 'none' }}
            onChange={handleFilesSelected}
          />
          <button
            style={styles.attachBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={replying}
            type="button"
          >
            📎 Attach a file
          </button>
          <button
            style={{ ...styles.sendBtn, opacity: replying ? 0.7 : 1 }}
            onClick={handleReply}
            disabled={replying}
          >
            {replying ? 'Sending…' : 'Send Reply'}
          </button>
        </div>

        <p style={styles.sessionNote}>
          For your security, this session will automatically end after 30 minutes of inactivity.
        </p>
      </div>
    </div>
  );
}

const F = 'var(--ci-font)';

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight:  '100vh',
    background: 'var(--ci-surface)',
    fontFamily: F,
  },
  secureBanner: {
    background:     'var(--ci-text)',
    color:          'rgba(255,255,255,0.82)',
    padding:        '9px 20px',
    fontSize:       12,
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    letterSpacing:  '0.01em',
    fontFamily:     F,
  },
  logoutBtn: {
    background:   'rgba(255,255,255,0.12)',
    border:       '1px solid rgba(255,255,255,0.18)',
    color:        'rgba(255,255,255,0.7)',
    padding:      '4px 12px',
    borderRadius: 6,
    cursor:       'pointer',
    fontSize:     11,
    fontFamily:   F,
    transition:   'background 0.15s',
  },
  container: {
    maxWidth: 680,
    margin:   '0 auto',
    padding:  '28px 16px 56px',
  },
  fullCenter: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    minHeight:      '100vh',
    background:     '#fafaf8',
  },
  card: {
    background:   '#fff',
    borderRadius: 14,
    padding:      '28px',
    maxWidth:     400,
    width:        '100%',
    border:       '1px solid #e5e2dd',
    textAlign:    'center',
    fontFamily:   F,
  },
  muted: { color: '#9e9eaa', fontSize: 14, fontFamily: F },
  header: {
    background:   '#fff',
    borderRadius: 14,
    padding:      '22px 26px',
    marginBottom: 16,
    border:       '1px solid #e5e2dd',
  },
  subject:    { margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: 'var(--ci-text)', fontFamily: F },
  headerMeta: { margin: 0, fontSize: 13, color: 'var(--ci-text-muted)', fontFamily: F },
  thread: {
    display:       'flex',
    flexDirection: 'column',
    gap:           12,
    marginBottom:  24,
  },
  bubble: {
    borderRadius: 12,
    padding:      '14px 18px',
  },
  bubbleStaff: {
    background: '#ffffff',
    border:     '1px solid #e5e2dd',
  },
  bubblePatient: {
    background: '#eaf5f3',
    border:     '1px solid #9dcfc9',
    alignSelf:  'flex-end',
  },
  bubbleHeader: {
    display:        'flex',
    justifyContent: 'space-between',
    marginBottom:   8,
  },
  sender:     { fontSize: 12, fontWeight: 600, color: '#3d3d52', fontFamily: F },
  bubbleTime: { fontSize: 11, color: '#9e9eaa', fontFamily: F },
  bubbleBody: { margin: 0, fontSize: 15, color: '#1a1a2e', lineHeight: 1.75, whiteSpace: 'pre-wrap', fontFamily: F },
  attachments: {
    background:   '#fff',
    borderRadius: 14,
    padding:      '16px 20px',
    marginBottom: 20,
    border:       '1px solid #e5e2dd',
  },
  attachTitle: { margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#3d3d52', fontFamily: F },
  attachRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        8,
    fontSize:   14,
    color:      '#3d3d52',
    marginBottom: 8,
    fontFamily: F,
  },
  attachSize:   { color: '#9e9eaa', fontSize: 12 },
  downloadBtn: {
    marginLeft:   'auto',
    background:   'none',
    border:       '1px solid #e5e2dd',
    borderRadius: 6,
    padding:      '3px 12px',
    cursor:       'pointer',
    fontSize:     12,
    color:        '#3d3d52',
    fontFamily:   F,
    transition:   'border-color 0.15s',
  },
  bubbleAttachments: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  attachmentChip: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    background: '#fff', border: '1px solid #d7d2ca', borderRadius: 8,
    padding: '5px 11px', cursor: 'pointer', fontSize: 13, color: 'var(--ci-primary)',
    maxWidth: '100%', textAlign: 'left', fontFamily: F,
  },
  fileChips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 },
  fileChip: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: '#eaf5f3', border: '1px solid #9dcfc9', borderRadius: 8,
    padding: '4px 10px', fontSize: 13, color: '#1a5f57', fontFamily: F,
  },
  fileChipRemove: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#1a5f57', fontSize: 16, lineHeight: 1, padding: 0, fontFamily: F,
  },
  attachBtn: {
    width:        '100%',
    padding:      '10px',
    background:   '#fff',
    color:        '#3d3d52',
    border:       '1px solid #d7d2ca',
    borderRadius: 8,
    fontSize:     14,
    fontWeight:   500,
    cursor:       'pointer',
    fontFamily:   F,
    marginBottom: 10,
  },
  replySection: {
    background:   '#ffffff',
    borderRadius: 14,
    padding:      '20px 24px',
    marginBottom: 16,
    border:       '1px solid #e5e2dd',
  },
  replyTitle: { margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: '#1a2744', fontFamily: F },
  textarea: {
    width:        '100%',
    border:       '1px solid #e5e2dd',
    borderRadius: 8,
    padding:      '10px 12px',
    fontSize:     14,
    color:        '#1a1a2e',
    resize:       'vertical',
    boxSizing:    'border-box',
    marginBottom: 10,
    fontFamily:   F,
    outline:      'none',
    transition:   'border-color 0.15s',
  },
  sendBtn: {
    width:        '100%',
    padding:      '12px',
    background:   'var(--ci-primary)',
    color:        '#fff',
    border:       'none',
    borderRadius: 8,
    fontSize:     15,
    fontWeight:   600,
    cursor:       'pointer',
    fontFamily:   F,
    letterSpacing: '0.01em',
    transition:   'background 0.15s',
  },
  errorBox: {
    background:   '#fef4f2',
    border:       '1px solid #f5c6bc',
    borderRadius: 8,
    padding:      '10px 14px',
    color:        '#c0392b',
    fontSize:     13,
    marginBottom: 10,
    fontFamily:   F,
  },
  sessionNote: {
    textAlign:  'center',
    fontSize:   12,
    color:      '#9e9eaa',
    lineHeight: 1.5,
    fontFamily: F,
  },
};
